import { isEmpty, isNumber } from './assertion.utils';

export interface IListFilterOptions<T = any> {
  filterMode?: 'start' | 'include';
  emptyQueryMode?: 'match all' | 'match none';
  maxResults?: number | 'all';
  filterPredicate?: (option: T) => T[];
  props?: string[];
}

const DEFAULT_FILTER_OPTIONS: IListFilterOptions<any> = {
  filterMode: 'include',
  emptyQueryMode: 'match all',
  maxResults: 'all'
};

/**
 * @description
 * a generic list filtering method. will filter a list of objects by any given criteria
 *
 * @param list the source list to filter
 * @param query a full or partial value of any of the list items object's properties
 * @param props name of the properties to compare values of in the filter predicate
 * @param options
 *    (1) filter mode: search value is matched from the start or anywhere in the target property
 *    (2) empty query mode: return full list or empty list when filter matched no results
 *    (3) max results: limit the number of items returned when results count exceeds this value
 * @returns search results - an array of items type same as list
 */

export function filterObjects<T extends { [key: string]: any }>(
  list: T[],
  query: string | number | boolean,
  options?: IListFilterOptions<T>
): Array<T> {
  options = { ...DEFAULT_FILTER_OPTIONS, ...options };

  const { filterMode, emptyQueryMode, maxResults, filterPredicate, props } = options;

  const defaultPredicate = (obj: T) => {
    const propertyValues = props.map((prop) => {
      if (prop in obj) {
        return obj[prop];
      }
    });

    return propertyValues.some((value) => {
      if (filterMode === 'include') {
        return value.toString().toLowerCase().includes(query.toString().toLowerCase());
      }
      return value.toString().toLowerCase().startsWith(query.toString().toLowerCase());
    });
  };

  let results = [] as T[];

  if (!isEmpty(query)) {
    results = list.filter(filterPredicate || defaultPredicate);
  } else if (emptyQueryMode === 'match all') {
    results = list;
  } else if (emptyQueryMode === 'match none') {
    results = [];
  }

  if (isNumber(maxResults) && results?.length) {
    results = results.slice(0, maxResults);
  }

  return results;
}

// export function filterObjects<T extends object, K extends keyof T>(
//   list: T[],
//   query: any,
//   props: K[],
//   options?: IListFilterOptions
// ): Array<T> {
//   options = { ...DEFAULT_FILTER_OPTIONS, ...options };

//   const { filterMode, emptyQueryMode, maxResults } = options;

//   const matchPredicate = (obj: T) => {
//     const propertyValues = props.map((prop) => obj[prop]);

//     return propertyValues.some((value) => {
//       if (filterMode === 'include') {
//         return value.toString().includes(query);
//       }
//       return value.toString().startsWith(query);
//     });
//   };

//   let results = [] as T[];

//   if (!isEmpty(query)) {
//     results = list.filter(matchPredicate);
//   } else if (emptyQueryMode === 'match all') {
//     results = list;
//   } else if (emptyQueryMode === 'match none') {
//     results = [];
//   }

//   if (isNumber(maxResults) && results?.length) {
//     results = results.slice(0, maxResults);
//   }

//   return results;
// }

/**
 * @description
 * a generic array merging method. will merge an array of objects by any given comparer function.
 * will replace the object at the right index if found by the comparer function.
 * will push the object to the returned array if it is missing in source.
 *
 * @param source the source array whose objects will be compared to
 * @param target the target array whose objects will replace or be pushed instead of source's objects if found worthy by the comparer
 * @param comparer the function on which to compare a source object with a target object, to find their index and decide if worthy of push/replace
 * @returns merged - the merged array of the given type
 */

export function mergeArrays<T>(source: T[], target: T[], comparer: (a: T, b: T) => boolean): T[] {
  const merged = [...source];

  for (const item of target) {
    const existingIndex = merged.findIndex((value) => comparer(value, item));
    if (existingIndex !== -1) {
      merged[existingIndex] = item;
    } else {
      merged.push(item);
    }
  }

  return merged;
}
